/**
 * TeamSection — project-scoped Team roster under Reference > Team.
 *
 * Three fixed groups, matching the firm's own structure:
 *   Alder James (our staff) > Client > Subconsultants.
 * Grouping is derived, not stored — see services/team.js#groupTeamRows. Adding people opens
 * TeamMemberPickerModal (an org's people, checkbox add/remove — no per-row "Role" field, it's
 * deprecated). Removing a person deletes their Project Teams row.
 *
 * Subconsultants follows a candidate → award lifecycle (mirrors Bid Campaigns/Construction
 * Contracts): "Add subconsultant" creates a Candidate-status Project Consultants row (org +
 * discipline, no people yet); "Award" flips it to Awarded, which is what turns it into a real
 * branch with people assignable. A subconsultant branch's existence never depends on a person
 * already being on it — see services/projectConsultants.js.
 *
 * Each Candidate now carries its own RFP -> Proposal -> Client Approval lifecycle
 * (services/consultantRfps.js, docs/TEAM.md): "Request proposal" generates a native PDF request
 * and emails the picked recipients (RfpRow children); "Log proposal received" records what came
 * back (fee/date/file, ProposalRow children); "Record client decision" is an internal log (no
 * client-facing page, no e-sign — subconsultants contract directly with the owner). Award stays
 * ungated — it's a soft nudge, not a hard requirement, so real-world exceptions aren't blocked.
 */

import { useEffect, useReducer, useRef, useState } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'
import {
  getProjectTeamRows, getOrganizations, getProjectClientOrgId, groupTeamRows, removeTeamMember,
} from '../services/team.js'
import { getProjectConsultants, awardConsultant, removeConsultantRow } from '../services/projectConsultants.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import {
  getProjectConsultantRfpData, deriveCandidateStage, candidateRfps, candidateProposals, rfpProposals,
  deleteConsultantRfp, deleteConsultantProposal, CLIENT_DECISION_LABELS,
  REVIEW_STATUS_LABELS, defaultHireBy, setConsultantHireBy, attachSignedCopy, signedCopyOk, SIGNED_COPY_ACCEPT,
} from '../services/consultantRfps.js'
import { confirmDialog, alertDialog, promptDialog } from './Dialog.jsx'
import TeamMemberPickerModal from './TeamMemberPickerModal.jsx'
import AddSubconsultantCandidateModal from './AddSubconsultantCandidateModal.jsx'
import SendConsultantRfpModal from './SendConsultantRfpModal.jsx'
import LogConsultantProposalModal from './LogConsultantProposalModal.jsx'
import RecordClientDecisionModal from './RecordClientDecisionModal.jsx'
import TeamRfpRecord, { RfpRecordRows } from './TeamRfpRecord.jsx'
import LocalResourcesGroup, { LocalResourcesList, ShopOrdersList, PrintOrderRecordRows } from './TeamLocalResources.jsx'
import PrintOrderModal from './PrintOrderModal.jsx'
import {
  getProjectPrintOrders, reproShops, splitShopsByCity, setShopCities, parseCities, setPrintOrderStatus, deletePrintOrder,
} from '../services/printOrders.js'

/* ── Visibility ─────────────────────────────────────────────────────────── */

function calcTeamVis() {
  if (!wf.zoomProjectId) return 'hidden'
  const clean = !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomCat && !wf.zoomItemId &&
                !wf.zoomConceptId && !wf.searchQuery
  if (wf.zoomSection && wf.zoomSection.indexOf('candidate-detail:') === 0 && clean) return 'candidateZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('rfp-detail:') === 0 && clean) return 'rfpZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('proposal-detail:') === 0 && clean) return 'proposalZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('sec:team:org:') === 0 && clean) return 'orgZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('sec:team:shop:') === 0 && clean) return 'shopZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('sec:team:printorder:') === 0 && clean) return 'printOrderZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('sec:team:group:') === 0 && clean) return 'groupZoom'
  if (wf.zoomSection) return 'hidden'
  if (wf.zoomRoot === 'team' && clean) return 'rootZoom'
  if (wf.zoomRoot === 'reference' && clean) return 'referenceZoom'
  if (!wf.zoomRoot && clean) return 'home'
  return 'hidden'
}

/** Resolve a candidate's { projectConsultantId, org, label, status } from raw data — works for
 * any status (not just current candidates), so a candidate-detail zoom stays valid after Award. */
function resolveCandidateById(id) {
  const pc = (ctx.teamProjectConsultants || []).find(p => p.id === id)
  if (!pc) return null
  const org = (ctx.teamOrganizations || []).find(o => o.id === pc.orgId)
  if (!org) return null
  const label = (pc.consultantService || 'Consultant') + ' - ' + (org.fields['Organization Name'] || 'Unknown')
  return { projectConsultantId: pc.id, org, label, status: pc.status, consultantService: pc.consultantService }
}

/* ── Filler row (loading / empty, standard outline grid) ────────────────── */

function FillerRow({ text, italic }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: '#7a9aaa', ...(italic ? { fontStyle: 'italic' } : {}) }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

/* ── A person row (leaf — no children, no zoom) ──────────────────────────── */

function MemberRow({ member, onRemove }) {
  const menu = useActionMenu()
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{member.name}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => onRemove(member)} danger>Remove from team</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

function MembersList({ members, onRemove, emptyText }) {
  if (!members) return <FillerRow text="Loading…" />
  if (members.length === 0) return <FillerRow text={emptyText} italic />
  return members.map(m => <MemberRow key={m.rowId} member={m} onRemove={onRemove} />)
}

/* ── A single proposal (leaf under an RfpRow — fee, date, client decision) ── */

function decisionBadge(proposal) {
  if (!proposal.client_decision) return null
  const label = CLIENT_DECISION_LABELS[proposal.client_decision] || proposal.client_decision
  const color = proposal.client_decision === 'approved' ? '#2e7d4f' : proposal.client_decision === 'declined' ? '#C0392B' : '#c79a3a'
  const icon = proposal.client_decision === 'approved' ? '✓' : proposal.client_decision === 'declined' ? '✕' : '🔁'
  return <span style={{ fontSize: 10, color, fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>{icon} {label}</span>
}

/** Where a proposal stands since proposals file themselves (docs/TEAM.md, 2026-11-02). Null for older rows. */
function reviewBadge(p) {
  if (!p.review_status) return null
  const label = p.signed_at ? (p.signed_via === 'upload' ? 'Signed copy on file' : 'Signed with them directly')
    : p.review_status === 'sent_to_client' && p.client_decision === 'approved' ? 'Approved on the Client Hub · signed copy to come'
    : p.review_status === 'sent_to_client' && p.client_decision === 'declined' ? 'Passed on by the client'
    : p.review_status === 'sent_to_client' ? 'On the Client Hub'
    : REVIEW_STATUS_LABELS[p.review_status] || p.review_status
  const color = p.signed_at || p.review_status === 'accepted' ? '#2e7d4f' : p.review_status === 'declined' ? '#C0392B' : '#c79a3a'
  return <span style={{ fontSize: 10, color, fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>{label}{p.source === 'email' ? ' · came in by email' : ''}</span>
}

function ProposalRow({ proposal, onEdit, onRecordDecision, onDelete }) {
  const menu = useActionMenu()
  const fileRef = useRef(null)
  // The owner sent us their signed copy (email, a photo of the paper): attach it here and it goes on
  // to the consultant in the owner's name, as if they had uploaded it on the Hub.
  const canAttach = !!proposal.review_status && proposal.review_status !== 'declined' && !proposal.signed_file_url && proposal.hire_by !== 'firm'
  async function attach(file) {
    if (!file) return
    if (!signedCopyOk(file)) { await alertDialog({ title: 'Not attached', message: 'Attach a PDF or a photo of the signed page.' }); return }
    if (!(await confirmDialog({ title: 'Attach the signed copy?', message: `${file.name} is saved on the proposal and emailed to the consultant in the owner\u2019s name, with the owner copied. The consultant is awarded on the Team.`, confirmLabel: 'Attach and send' }))) return
    try {
      const out = await attachSignedCopy(proposal.id, file, ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Name'])
      if (ctx.consultantRfpCache) ctx.consultantRfpCache = { ...ctx.consultantRfpCache, proposals: (ctx.consultantRfpCache.proposals || []).map(x => x.id === proposal.id ? { ...x, signed_at: new Date().toISOString(), signed_via: 'staff', signed_file_name: file.name, client_decision: 'approved' } : x) }
      window.dispatchEvent(new Event('outline-render'))
      await alertDialog({ title: 'Signed copy attached', message: out.done })
    } catch (e) { await alertDialog({ title: 'Not attached', message: e.message }) }
  }
  const fee = proposal.proposed_fee != null ? '$' + Number(proposal.proposed_fee).toLocaleString('en-US', { maximumFractionDigits: 0 }) : 'No fee entered'
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <button className="wf-bullet wf-bullet--item" title="View proposal" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection('proposal-detail:' + proposal.id, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{fee}</span>
          <span className="wf-meta" style={{ marginLeft: 8, color: '#7a9aaa', fontSize: 11 }}>{proposal.date_received || ''}</span>
          <span className="wf-meta" style={{ marginLeft: 8 }}>{proposal.review_status ? reviewBadge(proposal) : decisionBadge(proposal)}</span>
          {proposal.file_url && <a className="wf-meta" href={proposal.file_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ marginLeft: 8, fontSize: 11 }}>PDF</a>}
          {proposal.signed_file_url && <a className="wf-meta" href={proposal.signed_file_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ marginLeft: 8, fontSize: 11 }}>Signed copy</a>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => onEdit(proposal)}>Edit proposal</ActionMenuItem>
        <ActionMenuItem onClick={() => onRecordDecision(proposal)}>Record client decision</ActionMenuItem>
        {canAttach && <ActionMenuItem onClick={() => fileRef.current && fileRef.current.click()}>Attach signed copy…</ActionMenuItem>}
        <ActionMenuItem onClick={() => onDelete(proposal)} danger>Delete proposal</ActionMenuItem>
      </ActionMenu>
      <input ref={fileRef} type="file" accept={SIGNED_COPY_ACCEPT} style={{ display: 'none' }} onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; attach(f) }} />
    </div>
  )
}

function ProposalsList({ proposals, onEdit, onRecordDecision, onDelete, emptyText }) {
  if (!proposals || proposals.length === 0) return <FillerRow text={emptyText || 'No proposals logged yet.'} italic />
  return proposals.map(p => <ProposalRow key={p.id} proposal={p} onEdit={onEdit} onRecordDecision={onRecordDecision} onDelete={onDelete} />)
}

/* ── An RFP round (child of a CandidateRow — children = its Proposals) ──── */

function RfpRow({ rfp, proposals, onLogProposal, onEditProposal, onRecordDecision, onDeleteProposal, onDeleteRfp }) {
  const key = 'sec:team:rfp:' + rfp.id
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  const recipientCount = (rfp.recipients || []).length
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="View RFP" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection('rfp-detail:' + rfp.id, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">RFP sent {rfp.sent_at ? String(rfp.sent_at).slice(0, 10) : ''}</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{recipientCount} recipient{recipientCount !== 1 ? 's' : ''}</span>
          {rfp.due_date && <span className="wf-meta" style={{ marginLeft: 8, color: '#7a9aaa', fontSize: 11 }}>due {rfp.due_date}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => onLogProposal(rfp.id)}>Log proposal for this round</ActionMenuItem>
        <ActionMenuItem onClick={() => onDeleteRfp(rfp)} danger>Delete RFP</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          <TeamRfpRecord rfp={rfp} />
          <ProposalsList proposals={proposals} onEdit={onEditProposal} onRecordDecision={onRecordDecision} onDelete={onDeleteProposal}
            emptyText="No proposal logged for this round yet." />
        </div>
      )}
    </div>
  )
}

/* ── A candidate subconsultant row (children = its RFP rounds) ──────────── */

function CandidateRow({ candidate, rfps, proposals, onAward, onRemove, onRequestProposal, onLogProposal, onEditProposal, onRecordDecision, onDeleteProposal, onDeleteRfp }) {
  const key = 'sec:team:candidate:' + candidate.projectConsultantId
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  const stage = deriveCandidateStage(candidate.projectConsultantId, rfps, proposals)
  const myRfps = candidateRfps(candidate.projectConsultantId, rfps)
  // Who hires them decides where their proposal goes after review: the client's Hub, or straight to award.
  const hireMap = (ctx.consultantRfpCache && ctx.consultantRfpCache.hireBy) || {}
  const hireBy = hireMap[candidate.projectConsultantId] || defaultHireBy(candidate.consultantService)
  async function switchHire() {
    const next = hireBy === 'owner' ? 'firm' : 'owner'
    try {
      await setConsultantHireBy(candidate.projectConsultantId, next, ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Name'])
      if (ctx.consultantRfpCache) ctx.consultantRfpCache = { ...ctx.consultantRfpCache, hireBy: { ...hireMap, [candidate.projectConsultantId]: next } }
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Not changed', message: e.message }) }
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title={'Focus on ' + candidate.label} data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection('candidate-detail:' + candidate.projectConsultantId, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{candidate.label}</span>
          <span className="wf-meta"><span style={{ fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif', fontWeight: 600 }}>{stage.icon} {stage.label}</span></span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => onRequestProposal(candidate)}>Request proposal</ActionMenuItem>
        <ActionMenuItem onClick={() => onLogProposal(candidate)}>Log proposal received</ActionMenuItem>
        <ActionMenuItem onClick={() => onAward(candidate)}>Award — move onto the team</ActionMenuItem>
        <ActionMenuItem onClick={switchHire}>{hireBy === 'owner' ? 'The owner hires them · switch to: we hire them' : 'We hire them · switch to: the owner hires them'}</ActionMenuItem>
        <ActionMenuItem onClick={() => onRemove(candidate)} danger>Remove candidate</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {myRfps.length === 0
            ? <FillerRow text="No proposal requests sent yet. Use the ⋯ menu to request one." italic />
            : myRfps.map(r => (
                <RfpRow key={r.id} rfp={r} proposals={rfpProposals(r.id, proposals)}
                  onLogProposal={rfpId => onLogProposal(candidate, rfpId)} onEditProposal={onEditProposal}
                  onRecordDecision={onRecordDecision} onDeleteProposal={onDeleteProposal} onDeleteRfp={onDeleteRfp} />
              ))}
        </div>
      )}
    </div>
  )
}

/* ── Candidates branch — only rendered when at least one candidate exists ── */

function CandidatesBranch({ candidates, rfps, proposals, onAward, onRemove, onRequestProposal, onLogProposal, onEditProposal, onRecordDecision, onDeleteProposal, onDeleteRfp }) {
  const key = 'sec:team:group:subconsultants:candidates'
  const collapsed = wf.collapsed[key] !== false
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  if (!candidates || candidates.length === 0) return null
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--section"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Candidates</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{candidates.length}</span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {candidates.map(c => (
            <CandidateRow key={c.projectConsultantId} candidate={c} rfps={rfps} proposals={proposals}
              onAward={onAward} onRemove={onRemove} onRequestProposal={onRequestProposal} onLogProposal={onLogProposal}
              onEditProposal={onEditProposal} onRecordDecision={onRecordDecision} onDeleteProposal={onDeleteProposal} onDeleteRfp={onDeleteRfp} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── An awarded subconsultant organization branch (children = that org's people) ── */

function SubOrgNode({ sub, onRemoveMember, onAddMore, onRemoveSub }) {
  const key = 'sec:team:org:' + sub.org.id
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title={'Focus on ' + sub.label} data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{sub.label}</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{sub.members.length}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={onAddMore}>Add people from {sub.org.fields['Organization Name']}</ActionMenuItem>
        <ActionMenuItem onClick={() => onRemoveSub(sub)} danger>Remove subconsultant</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          <MembersList members={sub.members} onRemove={onRemoveMember} emptyText="No one from this org yet." />
        </div>
      )}
    </div>
  )
}

/* ── Alder James / Client group (children = people directly) ────────────── */

function SimpleGroupNode({ groupKey, label, members, onRemove, onAdd, addLabel, emptyText, extraMenuItems }) {
  const key = 'sec:team:group:' + groupKey
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title={'Focus on ' + label} data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {members && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{members.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {onAdd && <ActionMenuItem onClick={onAdd}>{addLabel || 'Add team members'}</ActionMenuItem>}
        {(extraMenuItems || []).map((item, i) => (
          <ActionMenuItem key={i} onClick={item.onClick} danger={item.danger}>{item.label}</ActionMenuItem>
        ))}
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          <MembersList members={members} onRemove={onRemove} emptyText={emptyText} />
        </div>
      )}
    </div>
  )
}

/* ── Subconsultants group (Candidates branch + one branch per awarded org) ── */

function SubconsultantsGroupNode({ candidates, subconsultants, rfps, proposals, onAward, onRemoveCandidate, onRemoveMember, onAddNew, onAddMore, onRemoveSub, onRequestProposal, onLogProposal, onEditProposal, onRecordDecision, onDeleteProposal, onDeleteRfp }) {
  const key = 'sec:team:group:subconsultants'
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  const total = subconsultants ? subconsultants.reduce((n, s) => n + s.members.length, 0) : null
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Focus on Subconsultants" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Subconsultants</span>
          {total != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{total}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={onAddNew}>Add subconsultant</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {!subconsultants && <FillerRow text="Loading…" />}
          <CandidatesBranch candidates={candidates} rfps={rfps} proposals={proposals} onAward={onAward} onRemove={onRemoveCandidate}
            onRequestProposal={onRequestProposal} onLogProposal={onLogProposal} onEditProposal={onEditProposal}
            onRecordDecision={onRecordDecision} onDeleteProposal={onDeleteProposal} onDeleteRfp={onDeleteRfp} />
          {subconsultants && subconsultants.length === 0 && (!candidates || candidates.length === 0) &&
            <FillerRow text="No subconsultants added yet. Use the ⋯ menu to add one." italic />}
          {subconsultants && subconsultants.map(sub => (
            <SubOrgNode key={sub.org.id} sub={sub} onRemoveMember={onRemoveMember} onAddMore={() => onAddMore(sub.org)} onRemoveSub={onRemoveSub} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function TeamSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const [vis, setVis] = useState(calcTeamVis)
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)
  const [picker, setPicker] = useState(null) // { scope: 'alder-james'|'client'|'subconsultant', org? } | null
  const [addingCandidate, setAddingCandidate] = useState(false)
  const [requestingFor, setRequestingFor] = useState(null) // candidate | null
  const [loggingFor, setLoggingFor] = useState(null)       // { candidate, defaultRfpId? } | null
  const [editingProposal, setEditingProposal] = useState(null) // { candidate, proposal } | null
  const [decidingProposal, setDecidingProposal] = useState(null) // proposal | null
  const [printing, setPrinting] = useState(null) // { org, initial? } | null
  const loadedRef = useRef(false)
  const actionsRef = useRef({}) // latest handlers, for the ZoomTitle CustomEvent bridge (see below)

  function recompute() {
    if (ctx.teamRows && ctx.teamOrganizations && ctx.teamClientOrgId !== undefined && ctx.teamProjectConsultants && ctx.teamContracts) {
      ctx.teamGrouped = groupTeamRows(ctx.teamRows, ctx.teamOrganizations, ctx.teamClientOrgId, ctx.teamProjectConsultants, ctx.teamContracts)
    }
  }

  function load(projId) {
    if (!projId || loadedRef.current) return
    loadedRef.current = true
    ctx.teamRows = null; ctx.teamOrganizations = null; ctx.teamClientOrgId = undefined
    ctx.teamProjectConsultants = null; ctx.teamContracts = null; ctx.teamGrouped = null; ctx.consultantRfpCache = null
    ctx.printOrderCache = null; ctx.printShopCities = null; ctx.printProjectCity = ''
    getProjectPrintOrders(projId)
      .then(d => { ctx.printOrderCache = d.orders; ctx.printShopCities = d.shopCities; ctx.printProjectCity = d.projectCity; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.printOrderCache = []; ctx.printShopCities = {}; window.dispatchEvent(new Event('outline-render')) })
    getProjectTeamRows(projId)
      .then(rows => { ctx.teamRows = rows; recompute(); window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.teamRows = []; recompute(); window.dispatchEvent(new Event('outline-render')) })
    getOrganizations()
      .then(orgs => { ctx.teamOrganizations = orgs; recompute(); window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.teamOrganizations = []; recompute(); window.dispatchEvent(new Event('outline-render')) })
    getProjectClientOrgId(projId)
      .then(id => { ctx.teamClientOrgId = id; recompute(); window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.teamClientOrgId = null; recompute(); window.dispatchEvent(new Event('outline-render')) })
    getProjectContracts(projId)
      .then(rows => { ctx.teamContracts = rows; recompute(); window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.teamContracts = []; recompute(); window.dispatchEvent(new Event('outline-render')) })
    getProjectConsultants(projId)
      .then(rows => {
        ctx.teamProjectConsultants = rows; recompute(); window.dispatchEvent(new Event('outline-render'))
        // Chained (not parallel): the RFP/proposal fetch needs every candidate id up front so
        // proposals logged with no RFP round on file still resolve — see api/consultant-rfps.js.
        return getProjectConsultantRfpData(projId, rows.map(pc => pc.id))
      })
      .then(data => { ctx.consultantRfpCache = data; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => {
        ctx.teamProjectConsultants = ctx.teamProjectConsultants || []
        ctx.consultantRfpCache = { rfps: [], proposals: [] }
        recompute(); window.dispatchEvent(new Event('outline-render'))
      })
  }

  function reload() {
    loadedRef.current = false
    ctx.teamRows = undefined
    load(recordId)
  }

  // Reset + reload on project change
  useEffect(() => {
    if (!recordId) return
    loadedRef.current = false
    load(recordId)
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load whenever on-screen (covers page refresh into Team)
  useEffect(() => {
    if (vis === 'hidden' || !recordId) return
    load(recordId)
  }, [vis, recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => (prev !== newRecordId ? newRecordId : prev))
      setVis(calcTeamVis())
      forceUpdate()
    }
    // Bridge from ZoomTitle's action menus (candidate-detail/rfp-detail/proposal-detail zooms) —
    // those are a sibling component, so they reach TeamSection's handlers via CustomEvents rather
    // than props. actionsRef always holds the latest render's handlers (assigned below, every
    // render, before any early return) so these listeners never see a stale closure.
    function onCandidateAction(e) {
      const { action, projectConsultantId, rfpId } = e.detail || {}
      const candidate = resolveCandidateById(projectConsultantId)
      if (!candidate) return
      if (action === 'request') actionsRef.current.setRequestingFor(candidate)
      else if (action === 'log') actionsRef.current.openLogProposal(candidate, rfpId)
      else if (action === 'award') actionsRef.current.handleAward(candidate)
      else if (action === 'remove') actionsRef.current.handleRemoveCandidate(candidate)
    }
    function onRfpAction(e) {
      const { action, rfpId } = e.detail || {}
      const rfp = ((ctx.consultantRfpCache && ctx.consultantRfpCache.rfps) || []).find(r => r.id === rfpId)
      if (!rfp) return
      if (action === 'log') actionsRef.current.openLogProposal(resolveCandidateById(rfp.project_consultant_airtable_id), rfpId)
      else if (action === 'delete') actionsRef.current.handleDeleteRfp(rfp)
    }
    function onProposalAction(e) {
      const { action, proposalId } = e.detail || {}
      const proposal = ((ctx.consultantRfpCache && ctx.consultantRfpCache.proposals) || []).find(p => p.id === proposalId)
      if (!proposal) return
      if (action === 'edit') actionsRef.current.setEditingProposal({ proposal })
      else if (action === 'decide') actionsRef.current.setDecidingProposal(proposal)
      else if (action === 'delete') actionsRef.current.handleDeleteProposal(proposal)
    }
    window.addEventListener('outline-render', onRender)
    window.addEventListener('open-team-candidate-action', onCandidateAction)
    window.addEventListener('open-team-rfp-action', onRfpAction)
    window.addEventListener('open-team-proposal-action', onProposalAction)
    function onPrintAction(e) {
      const { action, orgId, orderId } = e.detail || {}
      const pa = actionsRef.current.printActions
      if (!pa) return
      if (action === 'prepare') {
        const org = (ctx.teamOrganizations || []).find(o => o.id === orgId)
        if (org) pa.prepare(org)
        return
      }
      const order = (ctx.printOrderCache || []).find(o => o.id === orderId)
      if (!order) return
      if (action === 'again') pa.orderAgain(order)
      else if (action === 'delete') pa.deleteOrder(order)
      else if (action === 'status') pa.setStatus(order, e.detail.status)
    }
    window.addEventListener('open-team-print-action', onPrintAction)
    return () => {
      window.removeEventListener('open-team-print-action', onPrintAction)
      window.removeEventListener('outline-render', onRender)
      window.removeEventListener('open-team-candidate-action', onCandidateAction)
      window.removeEventListener('open-team-rfp-action', onRfpAction)
      window.removeEventListener('open-team-proposal-action', onProposalAction)
    }
  }, [])

  async function handleRemove(member) {
    if (!(await confirmDialog({
      title: 'Remove from team?',
      message: `Remove ${member.name} from this project's team?`,
      confirmLabel: 'Remove', danger: true,
    }))) return
    try { await removeTeamMember(member.rowId) } catch (e) { await alertDialog({ title: 'Remove failed', message: e.message || String(e) }); return }
    reload()
  }

  async function handleAward(candidate) {
    const hasApproved = candidateProposals(candidate.projectConsultantId, ctx.consultantRfpCache && ctx.consultantRfpCache.proposals)
      .some(p => p.client_decision === 'approved')
    if (!hasApproved && !(await confirmDialog({
      title: 'Award without a client-approved proposal?',
      message: 'No client-approved proposal is on file for this candidate yet. Award anyway?',
      confirmLabel: 'Award anyway',
    }))) return
    try { await awardConsultant(candidate.projectConsultantId) } catch (e) { await alertDialog({ title: 'Award failed', message: e.message || String(e) }); return }
    reload()
  }

  async function handleRemoveCandidate(candidate) {
    if (!(await confirmDialog({
      title: 'Remove candidate?',
      message: `Remove ${candidate.label} as a candidate for this project?`,
      confirmLabel: 'Remove', danger: true,
    }))) return
    try { await removeConsultantRow(candidate.projectConsultantId) } catch (e) { await alertDialog({ title: 'Remove failed', message: e.message || String(e) }); return }
    reload()
  }

  async function handleRemoveSub(sub) {
    const n = sub.members.length
    if (!(await confirmDialog({
      title: 'Remove subconsultant?',
      message: `Remove ${sub.label} from this project?` + (n ? ` Their ${n} team member${n !== 1 ? 's' : ''} will no longer show under Subconsultants (their Project Teams rows are kept, not deleted).` : ''),
      confirmLabel: 'Remove', danger: true,
    }))) return
    try { await removeConsultantRow(sub.projectConsultantId) } catch (e) { await alertDialog({ title: 'Remove failed', message: e.message || String(e) }); return }
    reload()
  }

  // Opens the same modal the Construction → Contract ⋯ menu uses (ContractorPortalLinkModal.jsx) —
  // one entry point for portal access, reachable from either place.
  function openPortalAccess(contractId, contractorName) {
    window.dispatchEvent(new CustomEvent('open-contractor-portal-link', { detail: { contractId, contractorName } }))
  }

  function openLogProposal(candidate, defaultRfpId) {
    setLoggingFor({ candidate, defaultRfpId: typeof defaultRfpId === 'string' ? defaultRfpId : undefined })
  }

  async function handleDeleteRfp(rfp) {
    if (!(await confirmDialog({
      title: 'Delete RFP round?',
      message: 'Delete this RFP round? Any proposals already logged against it are kept, just no longer tied to a round.',
      confirmLabel: 'Delete', danger: true,
    }))) return
    try { await deleteConsultantRfp(rfp.id) } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message || String(e) }); return }
    reload()
  }

  async function handleDeleteProposal(proposal) {
    if (!(await confirmDialog({
      title: 'Delete proposal?',
      message: 'Delete this logged proposal? This cannot be undone.',
      confirmLabel: 'Delete', danger: true,
    }))) return
    try { await deleteConsultantProposal(proposal.id) } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message || String(e) }); return }
    reload()
  }

  /* ── Print orders (Local Resources) ── */
  function reloadPrintOrders() {
    getProjectPrintOrders(recordId)
      .then(d => { ctx.printOrderCache = d.orders; ctx.printShopCities = d.shopCities; ctx.printProjectCity = d.projectCity; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => {})
  }
  const printActions = {
    prepare: org => setPrinting({ org }),
    editCities: async org => {
      const current = ((ctx.printShopCities || {})[org.id] || []).join(', ')
      const own = org.fields['Client City'] || ''
      const v = await promptDialog({
        title: 'Cities ' + (org.fields['Organization Name'] || 'this shop') + ' serves',
        message: 'City names separated by commas. Projects in these cities' + (own ? ' (and in ' + own + ', where the shop is)' : '') +
          ' list this shop first; it moves to "Other shops" everywhere else. Leave it empty to show the shop on every project.',
        placeholder: 'e.g. Laguna Beach, Laguna Niguel, Dana Point',
        defaultValue: current,
        multiline: true,
      })
      if (v === null) return
      try {
        const cities = await setShopCities(org.id, parseCities(v))
        ctx.printShopCities = { ...(ctx.printShopCities || {}), [org.id]: cities }
        window.dispatchEvent(new Event('outline-render'))
      } catch (e) { await alertDialog({ title: 'Could not save', message: e.message || String(e) }) }
    },
    orderAgain: order => {
      const org = (ctx.teamOrganizations || []).find(o => o.id === order.org_airtable_id)
      if (org) setPrinting({ org, initial: order })
      else alertDialog({ title: 'Shop not found', message: (order.org_name || 'This shop') + ' is no longer in Contacts.' })
    },
    setStatus: async (order, status) => {
      let note = order.status_note || null
      if (status === 'estimate_received') {
        const v = await promptDialog({ title: 'Estimate received', message: 'The amount or anything worth keeping (optional).', placeholder: 'e.g. $420, ready Thursday', defaultValue: note || '' })
        if (v === null) return
        note = v.trim() || null
      }
      try { await setPrintOrderStatus(order.id, status, note) } catch (e) { await alertDialog({ title: 'Could not save', message: e.message || String(e) }); return }
      reloadPrintOrders()
    },
    deleteOrder: async order => {
      if (!(await confirmDialog({
        title: 'Delete this print order record?',
        message: 'This removes the record of what was sent to ' + (order.org_name || 'the shop') + '. It does not cancel anything at the shop.',
        confirmLabel: 'Delete record', danger: true,
      }))) return
      try { await deletePrintOrder(order.id) } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message || String(e) }); return }
      reloadPrintOrders()
    },
  }
  const allShops = ctx.teamOrganizations ? reproShops(ctx.teamOrganizations) : null
  const shopSplit = allShops && ctx.printShopCities ? splitShopsByCity(allShops, ctx.printShopCities, ctx.printProjectCity) : null
  const shops = shopSplit ? shopSplit.local : null
  const otherShops = shopSplit ? shopSplit.other : []

  actionsRef.current = {
    setRequestingFor, openLogProposal, handleAward, handleRemoveCandidate,
    handleDeleteRfp, handleDeleteProposal, setEditingProposal, setDecidingProposal, printActions,
  }

  if (vis === 'hidden') return null

  const grouped = ctx.teamGrouped
  const rfpData = ctx.consultantRfpCache || { rfps: [], proposals: [] }

  const modal = picker && (
    <TeamMemberPickerModal
      scope={picker.scope}
      org={picker.org}
      projectId={recordId}
      clientOrgId={ctx.teamClientOrgId}
      organizations={ctx.teamOrganizations || []}
      teamRows={ctx.teamRows || []}
      onClose={() => setPicker(null)}
      onChanged={reload}
    />
  )

  const candidateModal = addingCandidate && (
    <AddSubconsultantCandidateModal
      projectId={recordId}
      organizations={ctx.teamOrganizations || []}
      existingOrgIds={(ctx.teamProjectConsultants || []).map(pc => pc.orgId)}
      clientOrgId={ctx.teamClientOrgId}
      onClose={() => setAddingCandidate(false)}
      onAdded={() => { setAddingCandidate(false); reload() }}
    />
  )

  const requestModal = requestingFor && (
    <SendConsultantRfpModal
      projectId={recordId}
      projectConsultantId={requestingFor.projectConsultantId}
      org={requestingFor.org}
      consultantService={requestingFor.consultantService}
      onClose={() => setRequestingFor(null)}
      onSent={() => { setRequestingFor(null); reload() }}
    />
  )

  const logModal = loggingFor && (
    <LogConsultantProposalModal
      projectConsultantId={loggingFor.candidate.projectConsultantId}
      rfps={candidateRfps(loggingFor.candidate.projectConsultantId, rfpData.rfps)}
      defaultRfpId={loggingFor.defaultRfpId}
      onClose={() => setLoggingFor(null)}
      onAdded={() => { setLoggingFor(null); reload() }}
    />
  )

  const editProposalModal = editingProposal && (
    <LogConsultantProposalModal
      projectConsultantId={editingProposal.candidate ? editingProposal.candidate.projectConsultantId : editingProposal.proposal.project_consultant_airtable_id}
      existingProposal={editingProposal.proposal}
      onClose={() => setEditingProposal(null)}
      onAdded={() => { setEditingProposal(null); reload() }}
    />
  )

  const decisionModal = decidingProposal && (
    <RecordClientDecisionModal
      proposal={decidingProposal}
      onClose={() => setDecidingProposal(null)}
      onRecorded={() => { setDecidingProposal(null); reload() }}
    />
  )

  const printModal = printing && (
    <PrintOrderModal
      projectId={recordId}
      org={printing.org}
      initial={printing.initial}
      onClose={() => setPrinting(null)}
      onSent={order => {
        setPrinting(null)
        // open the shop and the new order so the record is right there
        wf.collapsed['sec:team:group:local-resources'] = false
        wf.collapsed['sec:team:shop:' + order.org_airtable_id] = false
        saveViewState()
        reloadPrintOrders()
      }}
      onRecorded={reloadPrintOrders}
    />
  )

  const allModals = <>{modal}{candidateModal}{requestModal}{logModal}{editProposalModal}{decisionModal}{printModal}</>

  const groupNodes = (
    <>
      <SimpleGroupNode groupKey="alder-james" label="Alder James" members={grouped ? grouped.alderJames : null}
        onRemove={handleRemove} onAdd={() => setPicker({ scope: 'alder-james' })}
        emptyText="No Alder James team members added yet." />
      <SimpleGroupNode groupKey="client" label="Client" members={grouped ? grouped.client : null}
        onRemove={handleRemove} onAdd={() => setPicker({ scope: 'client' })}
        emptyText="No client contacts added yet." />
      <SimpleGroupNode groupKey="general-contractor" label="General Contractor"
        members={grouped ? (grouped.generalContractor ? grouped.generalContractor.members : []) : null}
        onRemove={handleRemove}
        onAdd={grouped && grouped.generalContractor ? () => setPicker({ scope: 'general-contractor', org: grouped.generalContractor.org }) : null}
        addLabel={grouped && grouped.generalContractor ? 'Add people from ' + grouped.generalContractor.org.fields['Organization Name'] : null}
        extraMenuItems={grouped && grouped.generalContractor ? [{ label: 'Manage portal access', onClick: () => openPortalAccess(grouped.generalContractor.contractId, grouped.generalContractor.label) }] : []}
        emptyText={grouped && !grouped.generalContractor
          ? 'No contract has a linked GC organization yet — link one from the contract (Construction → Contract ⋯ → Edit contract).'
          : 'No one from this org added yet.'} />
      <SubconsultantsGroupNode
        candidates={grouped ? grouped.candidates : null}
        subconsultants={grouped ? grouped.subconsultants : null}
        rfps={rfpData.rfps} proposals={rfpData.proposals}
        onAward={handleAward} onRemoveCandidate={handleRemoveCandidate} onRemoveMember={handleRemove}
        onAddNew={() => setAddingCandidate(true)} onAddMore={org => setPicker({ scope: 'subconsultant', org })}
        onRemoveSub={handleRemoveSub}
        onRequestProposal={c => setRequestingFor(c)} onLogProposal={openLogProposal}
        onEditProposal={p => setEditingProposal({ proposal: p })} onRecordDecision={p => setDecidingProposal(p)}
        onDeleteProposal={handleDeleteProposal} onDeleteRfp={handleDeleteRfp}
      />
      <LocalResourcesGroup shops={shops} otherShops={otherShops} actions={printActions} />
    </>
  )

  /* ── rootZoom — all three groups (no toolbar; Add lives on each group's ⋯) ── */
  if (vis === 'rootZoom') {
    return <div className="wf-list">{groupNodes}{allModals}</div>
  }

  /* ── groupZoom — one group's content only (ZoomTitle shows the group name) ── */
  if (vis === 'groupZoom') {
    const groupKey = wf.zoomSection.slice('sec:team:group:'.length)
    let content
    if (groupKey === 'alder-james') {
      content = <MembersList members={grouped ? grouped.alderJames : null} onRemove={handleRemove} emptyText="No Alder James team members added yet." />
    } else if (groupKey === 'client') {
      content = <MembersList members={grouped ? grouped.client : null} onRemove={handleRemove} emptyText="No client contacts added yet." />
    } else if (groupKey === 'local-resources') {
      content = <LocalResourcesList shops={shops} otherShops={otherShops} actions={printActions} />
    } else if (groupKey === 'general-contractor') {
      content = (
        <MembersList
          members={grouped ? (grouped.generalContractor ? grouped.generalContractor.members : []) : null}
          onRemove={handleRemove}
          emptyText={grouped && !grouped.generalContractor
            ? 'No contract has a linked GC organization yet.'
            : 'No one from this org added yet.'} />
      )
    } else {
      content = (
        <>
          {!grouped && <FillerRow text="Loading…" />}
          <CandidatesBranch candidates={grouped ? grouped.candidates : null} rfps={rfpData.rfps} proposals={rfpData.proposals}
            onAward={handleAward} onRemove={handleRemoveCandidate}
            onRequestProposal={c => setRequestingFor(c)} onLogProposal={openLogProposal}
            onEditProposal={p => setEditingProposal({ proposal: p })} onRecordDecision={p => setDecidingProposal(p)}
            onDeleteProposal={handleDeleteProposal} onDeleteRfp={handleDeleteRfp} />
          {grouped && grouped.subconsultants.length === 0 && grouped.candidates.length === 0 &&
            <FillerRow text="No subconsultants added yet." italic />}
          {grouped && grouped.subconsultants.map(sub => (
            <SubOrgNode key={sub.org.id} sub={sub} onRemoveMember={handleRemove}
              onAddMore={() => setPicker({ scope: 'subconsultant', org: sub.org })} onRemoveSub={handleRemoveSub} />
          ))}
        </>
      )
    }
    return <div className="wf-list">{content}{allModals}</div>
  }

  /* ── orgZoom — one subconsultant org's people (ZoomTitle shows the org label) ── */
  if (vis === 'orgZoom') {
    const orgId = wf.zoomSection.slice('sec:team:org:'.length)
    const sub = grouped ? grouped.subconsultants.find(s => s.org.id === orgId) : null
    return (
      <div className="wf-list">
        {!grouped && <FillerRow text="Loading…" />}
        {grouped && !sub && <FillerRow text="Organization not found." italic />}
        {sub && <MembersList members={sub.members} onRemove={handleRemove} emptyText="No one from this org yet." />}
        {modal}
      </div>
    )
  }

  /* ── shopZoom — one print shop's orders on this project (ZoomTitle shows the shop) ── */
  if (vis === 'shopZoom') {
    const orgId = wf.zoomSection.slice('sec:team:shop:'.length)
    const org = (ctx.teamOrganizations || []).find(o => o.id === orgId)
    return (
      <div className="wf-list">
        {!ctx.teamOrganizations && <FillerRow text="Loading…" />}
        {ctx.teamOrganizations && !org && <FillerRow text="Print shop not found." italic />}
        {org && <ShopOrdersList org={org} actions={printActions} />}
        {allModals}
      </div>
    )
  }

  /* ── printOrderZoom — one order as sent (ZoomTitle shows its date + status) ── */
  if (vis === 'printOrderZoom') {
    const id = wf.zoomSection.slice('sec:team:printorder:'.length)
    const order = (ctx.printOrderCache || []).find(o => o.id === id)
    return (
      <div className="wf-list">
        {!ctx.printOrderCache && <FillerRow text="Loading…" />}
        {ctx.printOrderCache && !order && <FillerRow text="Print order not found." italic />}
        {order && <PrintOrderRecordRows order={order} />}
        {allModals}
      </div>
    )
  }

  /* ── candidateZoom — one candidate's RFP rounds (ZoomTitle shows the candidate label) ── */
  if (vis === 'candidateZoom') {
    const id = wf.zoomSection.slice('candidate-detail:'.length)
    const candidate = resolveCandidateById(id)
    const myRfps = candidate ? candidateRfps(id, rfpData.rfps) : []
    return (
      <div className="wf-list">
        {!ctx.teamProjectConsultants && <FillerRow text="Loading…" />}
        {ctx.teamProjectConsultants && !candidate && <FillerRow text="Candidate not found." italic />}
        {candidate && myRfps.length === 0 && <FillerRow text="No proposal requests sent yet." italic />}
        {candidate && myRfps.map(r => (
          <RfpRow key={r.id} rfp={r} proposals={rfpProposals(r.id, rfpData.proposals)}
            onLogProposal={rfpId => openLogProposal(candidate, rfpId)} onEditProposal={p => setEditingProposal({ proposal: p })}
            onRecordDecision={p => setDecidingProposal(p)} onDeleteProposal={handleDeleteProposal} onDeleteRfp={handleDeleteRfp} />
        ))}
        {allModals}
      </div>
    )
  }

  /* ── rfpZoom — one RFP round's proposals (ZoomTitle shows the sent date + recipients) ── */
  if (vis === 'rfpZoom') {
    const id = wf.zoomSection.slice('rfp-detail:'.length)
    const rfp = (rfpData.rfps || []).find(r => r.id === id)
    return (
      <div className="wf-list">
        {!ctx.consultantRfpCache && <FillerRow text="Loading…" />}
        {ctx.consultantRfpCache && !rfp && <FillerRow text="RFP not found." italic />}
        {rfp && (
          <>
            <RfpRecordRows rfp={rfp} />
            <ProposalsList proposals={rfpProposals(id, rfpData.proposals)} onEdit={p => setEditingProposal({ proposal: p })}
              onRecordDecision={p => setDecidingProposal(p)} onDelete={handleDeleteProposal} />
          </>
        )}
        {allModals}
      </div>
    )
  }

  /* ── proposalZoom — a single proposal's detail (ZoomTitle shows fee + decision) ── */
  if (vis === 'proposalZoom') {
    const id = wf.zoomSection.slice('proposal-detail:'.length)
    const proposal = (rfpData.proposals || []).find(p => p.id === id)
    return (
      <div className="wf-list">
        {!ctx.consultantRfpCache && <FillerRow text="Loading…" />}
        {ctx.consultantRfpCache && !proposal && <FillerRow text="Proposal not found." italic />}
        {proposal && (
          <>
            <FillerRow text={'Date received: ' + (proposal.date_received || '—')} />
            {proposal.notes && <FillerRow text={proposal.notes} italic />}
            {proposal.file_url && <FillerRow text={'File: ' + proposal.file_url} />}
            {proposal.client_decision && (
              <FillerRow text={'Client decision: ' + (CLIENT_DECISION_LABELS[proposal.client_decision] || proposal.client_decision) +
                (proposal.client_decision_note ? ' — ' + proposal.client_decision_note : '')} />
            )}
          </>
        )}
        {allModals}
      </div>
    )
  }

  /* ── home / referenceZoom — Reference-child root node ─────────────────── */
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null

  const rootCollapsed = wf.collapsed['root:team'] !== false
  const totalCount = grouped
    ? grouped.alderJames.length + grouped.client.length + (grouped.generalContractor ? grouped.generalContractor.members.length : 0)
      + grouped.subconsultants.reduce((n, s) => n + s.members.length, 0)
    : null
  const rootNode = (
    <div className={`wf-node wf-node--root wf-node--root-team${rootCollapsed ? ' wf-node--collapsed' : ''}`} data-key="root:team">
      <div className="wf-row">
        <button
          className="wf-triangle"
          onClick={e => {
            e.stopPropagation()
            const nc = !rootCollapsed
            wf.collapsed['root:team'] = nc
            saveViewState()
            if (!nc) load(recordId)
            window.dispatchEvent(new Event('outline-render'))
          }}
        >
          <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--root"
          title="Focus on Team"
          data-fn="zoomTo"
          data-level="root"
          data-rootname="team"
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--root">Team</span>
          {totalCount != null && <span className="wf-prog" style={{ marginLeft: 8, color: '#7a9aaa' }}>{totalCount}</span>}
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="root"
          data-key="root:team"
          data-rootname="team"
        >&#8943;</button>
      </div>
      {!rootCollapsed && <div className="wf-children">{groupNodes}</div>}
    </div>
  )

  return (
    <div className="wf-list">
      {vis === 'referenceZoom' ? rootNode : <div className="wf-ref-child">{rootNode}</div>}
      {allModals}
    </div>
  )
}
